/** Bottom road section of the LUDOKA game board - Magma skin */

import type { RoadSectionProps } from '../../constants/types';
import { renderMultiplePositions } from '../basics/utils/utils';
import { BOTTOM_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from '../basics/constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for bottom section (Green)
 * Ordered top to bottom for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'GFINAL',
  'GS6',
  'GC6',
  'GF6',
  'GS5',
  'GC5',
  'GF5',
  'GS4',
  'GC4',
  'GF4',
  'GS3',
  'GC3',
  'GF3',
  'GS2',
  'GC2',
  'GF2',
  'GS1',
  'GC1',
  'GF1'
];

export default function MagmaBottomRoadSection({
  viewBox = '0 0 268 616',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Bottom road section with green final stretch"
    >
      {/* Dark shadow ground - base layer */}
      <path
        d="M18.5396 567.79V593.37C18.7296 597.83 19.3895 603.84 21.7295 607.66C25.0395 613.06 29.8696 611.07 33.3996 610.05C42.6996 607.35 39.1096 612.54 60.7495 612.31C74.6695 612.16 74.3796 610.04 87.0695 609.98C98.5295 609.93 99.1096 611.66 111.18 612.29C125.8 613.06 126.5 610.6 147.48 609.77C162.6 609.17 162.02 610.44 178.59 609.77C193.76 609.16 193.01 608.16 207.59 607.75C214.04 607.57 216.01 607.71 237.44 607.75C237.51 607.75 237.58 607.75 237.65 607.75C244.15 607.76 249.42 602.49 249.43 595.98L249.46 580.36L18.5495 567.8L18.5396 567.79Z"
        fill="url(#mgmB_paint0)"
      />

      {/* Green gradient ground overlay */}
      <path
        d="M249.43 567.94V583.52C249.43 590.05 244.15 595.34 237.64 595.33H237.44C234.47 595.32 231.87 595.32 229.58 595.31C227.48 595.31 225.62 595.29 223.98 595.29C218.66 595.26 215.62 595.23 213.12 595.24C211.36 595.25 209.88 595.27 208.05 595.32C207.9 595.32 207.74 595.33 207.58 595.33C205.27 595.4 203.34 595.47 201.67 595.56C201.43 595.57 201.21 595.59 200.98 595.6C195.19 595.94 192.61 596.42 187.27 596.84H187.25C185 597.02 182.24 597.2 178.57 597.34C172.89 597.57 169.23 597.57 166.27 597.5C163.58 597.43 161.45 597.31 158.92 597.23C157.98 597.2 156.99 597.18 155.9 597.18C154.29 597.18 152.48 597.18 150.28 597.24C149.4 597.26 148.46 597.3 147.47 597.34C146.93 597.36 146.41 597.38 145.91 597.41C126.45 598.3 125.41 600.61 111.17 599.86C110.1 599.8 109.12 599.74 108.22 599.67C102.61 599.23 99.8698 598.53 96.2998 598.06C94.9998 597.89 93.5998 597.75 91.9098 597.66C90.5098 597.58 88.9398 597.54 87.0498 597.55C85.8498 597.55 84.7798 597.58 83.7898 597.62C76.3198 597.91 74.1298 599.06 67.3998 599.6C65.9298 599.72 64.2498 599.82 62.2498 599.86C61.7598 599.86 61.2598 599.86 60.7298 599.88C55.1298 599.94 51.2298 599.64 48.3498 599.2C46.6098 598.94 45.2498 598.63 44.1098 598.33C40.7598 597.41 39.4098 596.52 36.3198 596.96C35.4898 597.07 34.5298 597.28 33.3898 597.62C29.8598 598.65 25.0298 600.64 21.7198 595.24C19.3898 591.42 18.7198 585.41 18.5298 580.95V555.37L113.81 560.54L133.74 561.63L166.13 563.39L194.41 564.93L227.76 566.74L246.66 567.76L249.42 567.94H249.43Z"
        fill="url(#mgmB_paint1)"
      />

      {/* Column 1 ground detail - green taper */}
      <path
        d="M108.23 599.67C102.62 599.23 99.8799 598.53 96.3099 598.06C89.7599 593.16 74.5599 581.88 59.8099 589.14C55.2299 591.39 51.5099 595.02 48.3699 599.2C46.6299 598.94 45.2699 598.63 44.1299 598.33C49.3999 588.99 51.3899 583.2 55.3399 579.06C55.5399 578.85 55.7499 578.64 55.9599 578.44C63.6799 571.09 74.2499 573.67 82.7799 577.94C89.6799 581.41 95.2499 585.98 96.8799 587.33C101.22 590.9 101.61 592.28 108.22 599.67H108.23Z"
        fill="url(#mgmB_paint2)"
      />

      {/* Column 3 ground detail - green taper */}
      <path
        d="M165.86 585.96C170.12 589.14 178.1 595.11 187.26 596.84C185.01 597.02 182.25 597.2 178.58 597.34C172.9 597.57 169.24 597.57 166.28 597.5C165.9 597.19 165.6 596.94 165.37 596.76C159.51 591.93 160.86 591.1 144.32 574.06C142.04 571.71 139.99 569.64 138.16 567.83C139.52 567.87 141 567.89 142.65 567.9C145.23 567.9 147.42 567.87 149.33 567.79C153.34 574.02 158.5 580.45 165.86 585.96Z"
        fill="url(#mgmB_paint3)"
      />

      {/* Column 4 ground detail - green taper */}
      <path
        d="M229.69 568.23C229.12 568.9 228.6 569.61 228.09 570.32C220.02 581.47 216.12 589.38 213.12 595.24C211.36 595.25 209.88 595.27 208.05 595.32C207.9 595.32 207.74 595.33 207.58 595.33C205.27 595.4 203.34 595.47 201.67 595.56C201.43 595.57 201.21 595.59 200.98 595.6C201.47 595.41 201.95 595.19 202.44 594.95C212.83 589.84 218.82 577.59 223.66 568.06C224.36 568.09 225.07 568.11 225.8 568.13C227.18 568.17 228.46 568.21 229.68 568.23H229.69Z"
        fill="url(#mgmB_paint4)"
      />

      {/* Row 1: Green gradient center and purple side squares */}
      <path
        d="M172.62 194.78V117.94H95.7796V194.78H172.62Z"
        fill="url(#mgmB_paint5)"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 1 center path - white square for pawn position */}
      <path
        d="M102.11 132.34V180.39C102.11 184.841 105.718 188.45 110.17 188.45H158.22C162.671 188.45 166.28 184.841 166.28 180.39V132.34C166.28 127.889 162.671 124.28 158.22 124.28H110.17C105.718 124.28 102.11 127.889 102.11 132.34Z"
        fill="white"
      />
      {/* Row 1 left - medium purple */}
      <path
        d="M95.7697 117.94H18.9297V194.78H95.7697V117.94Z"
        fill="#7E6489"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 1 right - medium purple */}
      <path
        d="M249.46 117.94H172.62V194.78H249.46V117.94Z"
        fill="#7E6489"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 2: Dark purple frame center and purple side squares */}
      <path
        d="M172.62 271.62V194.78H95.7796V271.62H172.62Z"
        fill="#563D4B"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 2 center path - white square for pawn position */}
      <path
        d="M102.11 209.18V257.23C102.11 261.681 105.718 265.29 110.17 265.29H158.22C162.671 265.29 166.28 261.681 166.28 257.23V209.18C166.28 204.729 162.671 201.12 158.22 201.12H110.17C105.718 201.12 102.11 204.729 102.11 209.18Z"
        fill="white"
      />
      {/* Row 2 left - light purple */}
      <path
        d="M95.7697 194.78H18.9297V271.62H95.7697V194.78Z"
        fill="#886F97"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 2 right - light purple */}
      <path
        d="M249.46 194.78H172.62V271.62H249.46V271.62Z"
        fill="#886F97"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 3: Green gradient center and purple side squares */}
      <path
        d="M172.62 348.47V271.63H95.7796V348.47H172.62Z"
        fill="url(#mgmB_paint6)"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 3 center path - white square for pawn position */}
      <path
        d="M102.1 286.02V334.07C102.1 338.521 105.708 342.13 110.16 342.13H158.21C162.661 342.13 166.27 338.521 166.27 334.07V286.02C166.27 281.568 162.661 277.96 158.21 277.96H110.16C105.708 277.96 102.1 281.568 102.1 286.02Z"
        fill="white"
      />
      {/* Row 3 left - medium purple */}
      <path
        d="M95.7697 271.62H18.9297V348.46H95.7697V271.62Z"
        fill="#7E6489"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 3 right - medium purple */}
      <path
        d="M249.46 271.62H172.62V348.46H249.46V271.62Z"
        fill="#7E6489"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 4: Dark purple frame center and purple side squares */}
      <path
        d="M172.62 425.31V348.47H95.7796V425.31H172.62Z"
        fill="#563D4B"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 4 center path - white square for pawn position */}
      <path
        d="M102.11 362.86V410.91C102.11 415.361 105.718 418.97 110.17 418.97H158.22C162.671 418.97 166.28 415.361 166.28 410.91V362.86C166.28 358.409 162.671 354.8 158.22 354.8H110.17C105.718 354.8 102.11 358.409 102.11 362.86Z"
        fill="white"
      />
      {/* Row 4 left - light purple */}
      <path
        d="M95.7697 348.46H18.9297V425.3H95.7697V348.46Z"
        fill="#886F97"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 4 right - light purple */}
      <path
        d="M249.46 348.46H172.62V425.3H249.46V425.3Z"
        fill="#886F97"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 5: Green gradient center, dark purple left, medium purple right */}
      <path
        d="M172.62 502.15V425.31H95.7796V502.15H172.62Z"
        fill="url(#mgmB_paint7)"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 5 center path - white square for pawn position */}
      <path
        d="M102.11 439.7V487.75C102.11 492.201 105.718 495.81 110.17 495.81H158.22C162.671 495.81 166.28 492.201 166.28 487.75V439.7C166.28 435.249 162.671 431.64 158.22 431.64H110.17C105.718 431.64 102.11 435.249 102.11 439.7Z"
        fill="white"
      />
      {/* Row 5 left - dark purple frame */}
      <path
        d="M95.7697 425.3H18.9297V502.14H95.7697V502.14Z"
        fill="#563D4B"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 5 right - medium purple */}
      <path
        d="M249.46 425.3H172.62V502.14H249.46V502.14Z"
        fill="#7E6489"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Row 6: Bottom rounded corners - purple frame */}
      {/* Left rounded corner - light purple */}
      <path
        d="M18.9297 502.14H95.7697V578.98H28.9297C23.4097 578.98 18.9297 574.5 18.9297 568.98V502.14Z"
        fill="#886F97"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Row 6 center - medium purple */}
      <path
        d="M172.62 502.14H95.7798V578.98H172.62V502.14Z"
        fill="#7E6489"
        stroke="black"
        strokeMiterlimit="10"
      />
      {/* Right rounded corner - light purple */}
      <path
        d="M172.62 502.14H249.46V568.98C249.46 574.5 244.98 578.98 239.46 578.98H172.62V502.14Z"
        fill="#886F97"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Star decoration - shadow layer + white top layer */}
      <path
        d="M219.57 377.09L212.95 365.14C212.31 363.99 210.67 363.94 209.97 365.06L202.74 376.65L189.33 379.25C188.03 379.5 187.49 381.05 188.33 382.06L197.12 392.52L195.45 406.08C195.29 407.39 196.59 408.39 197.82 407.9L210.48 402.77L222.86 408.55C224.06 409.11 225.41 408.18 225.32 406.86L224.35 393.23L233.67 383.24C234.57 382.27 234.1 380.7 232.82 380.38L219.56 377.09H219.57Z"
        fill="#303030"
      />
      {/* White top layer */}
      <path
        d="M219.57 375.78L212.95 363.83C212.31 362.68 210.67 362.63 209.97 363.75L202.74 375.34L189.33 377.94C188.03 378.19 187.49 379.74 188.33 380.75L197.12 391.21L195.45 404.77C195.29 406.08 196.59 407.08 197.82 406.59L210.48 401.46L222.86 407.24C224.06 407.8 225.41 406.87 225.32 405.55L224.35 391.92L233.67 381.93C234.57 380.96 234.1 379.39 232.82 379.07L219.56 375.78H219.57Z"
        fill="white"
      />

      {/* White center path square in left column row 5 (pawn position) */}
      <path
        d="M25.27 439.7L25.27 487.75C25.27 492.201 28.8786 495.81 33.33 495.81H81.38C85.8314 495.81 89.44 492.201 89.44 487.75V439.7C89.44 435.249 85.8314 431.64 81.38 431.64H33.33C28.8786 431.64 25.27 435.249 25.27 439.7Z"
        fill="white"
      />

      {/* Green gradient triangle/arrow at top */}
      <path
        d="M134.2 2.68005L249.46 117.94L18.9302 117.21L134.2 2.68005Z"
        fill="url(#mgmB_paint8)"
        stroke="black"
        strokeMiterlimit="10"
      />

      {/* Render pawns at all 19 positions - from top to bottom for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        1.0,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents
      )}

      <defs>
        {/* paint0: Dark shadow for ground base */}
        <linearGradient
          id="mgmB_paint0"
          x1="134"
          y1="596.13"
          x2="134"
          y2="622.82"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1A1A1A" />
          <stop offset="1" stopColor="#000000" />
        </linearGradient>

        {/* paint1: Green gradient for ground overlay */}
        <linearGradient
          id="mgmB_paint1"
          x1="18.5398"
          y1="577.69"
          x2="249.43"
          y2="577.69"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#128406" />
          <stop offset="1" stopColor="#2BBF02" />
        </linearGradient>

        {/* paint2: Green gradient for column 1 ground taper */}
        <linearGradient
          id="mgmB_paint2"
          x1="44.1299"
          y1="586.74"
          x2="108.23"
          y2="586.74"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>

        {/* paint3: Green gradient for column 3 ground taper */}
        <linearGradient
          id="mgmB_paint3"
          x1="138.16"
          y1="582.67"
          x2="187.26"
          y2="582.67"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>

        {/* paint4: Green gradient for column 4 ground taper */}
        <linearGradient
          id="mgmB_paint4"
          x1="200.99"
          y1="581.83"
          x2="229.69"
          y2="581.83"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>

        {/* paint5: Green gradient for Row 1 center */}
        <linearGradient
          id="mgmB_paint5"
          x1="134.2"
          y1="117.94"
          x2="134.2"
          y2="194.78"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>

        {/* paint6: Green gradient for Row 3 center */}
        <linearGradient
          id="mgmB_paint6"
          x1="134.2"
          y1="271.63"
          x2="134.2"
          y2="348.47"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>

        {/* paint7: Green gradient for Row 5 center */}
        <linearGradient
          id="mgmB_paint7"
          x1="134.2"
          y1="425.3"
          x2="134.2"
          y2="502.14"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>

        {/* paint8: Green gradient for top triangle arrow */}
        <linearGradient
          id="mgmB_paint8"
          x1="18.9302"
          y1="60.3101"
          x2="249.46"
          y2="60.3101"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#29B208" />
          <stop offset="0.52" stopColor="#7DFF0B" />
          <stop offset="1" stopColor="#25C909" />
        </linearGradient>
      </defs>
    </svg>
  );
}
